import { z } from 'zod';
import { AwardTypeCode, PaidFrom, PoolPosition, StageId } from './awards.js';
import { Cents, PlainDate } from './common.js';
import { PageInfo, pageFields } from './paging.js';
import { AreteValue } from './recognition.js';

/**
 * Insights (RPT1–RPT4, X6, F1; APP-045). Read-only reports, each scoped to what the reader may
 * see: the whole company for Executives, System Admins and (for the money reports) Finance and
 * Stock Admins; their own sector for a Sector Lead. Amounts are integer cents; no split fraction,
 * tax detail, write-up or internal state code is ever carried here.
 */

const Ref = z.object({ id: z.string(), name: z.string() });
const Count = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const Year = z.number().int().min(2000).max(2100);
const Month = z.string().regex(/^\d{4}-(?:0[1-9]|1[0-2])$/, 'must be YYYY-MM');
const Units = z.object({ cashCents: Cents, options: z.number().int() });
/** A whole percent, rounded half up; null when there's nothing to divide by. */
const Percent = z.number().int().min(0).nullable();

/** Repeated (`?type=PAA&type=TAA`) or comma-separated (`?type=PAA,TAA`) query values. */
const many = <T extends z.ZodType>(item: T) =>
  z.preprocess(
    (v) =>
      v === undefined || v === ''
        ? undefined
        : (Array.isArray(v) ? v : [v]).flatMap((x) =>
            String(x)
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s !== ''),
          ),
    z.array(item).max(20).optional(),
  );
const QueryYear = z.coerce.number().int().min(2000).max(2100);

/** Which reports exist. Ask the data (AI) and Team recognition are listed in the library. */
export const InsightsReportId = z.enum([
  'overview',
  'all_awards',
  'heat_map',
  'budgets_history',
  'spend',
  'spot_awards',
  'ask_data',
  'team_recognition',
]);
export type InsightsReportId = z.infer<typeof InsightsReportId>;

/** Whose numbers a report shows: the whole company, or the reader's own sector(s). */
export const ReportScope = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('company') }),
  z.object({ kind: z.literal('sectors'), sectors: z.array(Ref).min(1) }),
]);
export type ReportScope = z.infer<typeof ReportScope>;

/**
 * Spot Awards in the reader's Spot scope, this budget year to date (APP-045 A41). Before the first
 * one every Spot number is a real zero, never sample data.
 */
export const SpotStatus = z.object({
  /** Spot nominations are switched on (Admin › Award types & features). */
  enabled: z.boolean(),
  /** False until MyRewards records Spot Awards. */
  tracked: z.boolean(),
  /** False until the old Spot tracker is imported. */
  imported: z.boolean(),
  awards: Count,
  valueCents: Cents,
  /**
   * The Spot cash range as Admin has it set today (APP-102), for "From $500 to $3,000 each".
   * Absent on older servers.
   */
  range: z.object({ minCents: Cents, maxCents: Cents }).optional(),
});
export type SpotStatus = z.infer<typeof SpotStatus>;

// ---------------------------------------------------------------------------------------------
// Time frames
// ---------------------------------------------------------------------------------------------

/**
 * A report's time frame. `fiscal_year` is the year an award charges (its budget year), so it ties
 * to Budget pools; the others go by award date in the organization's time zone.
 */
export const PeriodKind = z.enum(['fiscal_year', 'last_12_months', 'range', 'any_time']);
export type PeriodKind = z.infer<typeof PeriodKind>;

export const ResolvedPeriod = z.object({
  kind: PeriodKind,
  /** Set for `fiscal_year`. */
  fiscalYear: Year.nullable(),
  /** The award-date bounds, inclusive. Null for `any_time`, and for `fiscal_year`. */
  from: PlainDate.nullable(),
  to: PlainDate.nullable(),
});
export type ResolvedPeriod = z.infer<typeof ResolvedPeriod>;

const periodFields = {
  period: PeriodKind.default('fiscal_year'),
  /** With `fiscal_year`; defaults to this year. */
  fiscalYear: QueryYear.optional(),
  /** With `range`. */
  from: PlainDate.optional(),
  to: PlainDate.optional(),
};
const checkPeriod = (
  v: { period: PeriodKind; from?: string | undefined; to?: string | undefined },
  ctx: z.RefinementCtx,
) => {
  if (v.period !== 'range') return;
  if (v.from === undefined || v.to === undefined)
    ctx.addIssue({
      code: 'custom',
      path: ['from'],
      message: 'A custom range needs a start and end',
    });
  else if (v.from > v.to)
    ctx.addIssue({ code: 'custom', path: ['to'], message: 'The end is before the start' });
};

// ---------------------------------------------------------------------------------------------
// X6 · All awards (the award register)
// ---------------------------------------------------------------------------------------------

/** "Where it is": the plain stages, plus awards that ended before payment. */
export const RegisterStage = z.enum([
  'draft',
  'approval',
  'with_stock_admin',
  'employee_choice',
  'paid',
  'closed',
  'ended',
]);
export type RegisterStage = z.infer<typeof RegisterStage>;

/**
 * `closed_newest`: closed awards by the newest close first, then the rest (APP-072).
 * `sent_newest`: by when each was sent, newest first; drafts never sent come after (X1's
 * "latest 10", K34, APP-054).
 */
export const RegisterSort = z.enum([
  'newest',
  'oldest',
  'value_desc',
  'value_asc',
  'award_id',
  'closed_newest',
  'sent_newest',
]);
export type RegisterSort = z.infer<typeof RegisterSort>;
export const RegisterGroupBy = z.enum(['none', 'sector', 'type', 'month']);
export type RegisterGroupBy = z.infer<typeof RegisterGroupBy>;

const registerFilterFields = {
  ...periodFields,
  type: many(AwardTypeCode),
  /** The award's sector. */
  sectorId: z.string().min(1).max(100).optional(),
  /** Awards with anyone paid from this pool (a split TAA matches both of its pools). */
  poolId: z.string().min(1).max(100).optional(),
  /** Omitted: every stage except awards that ended (rejected or cancelled). */
  stage: many(RegisterStage),
  programId: z.uuid().optional(),
  /** Awards this person received. */
  personId: z.uuid().optional(),
  /** Awards this person entered (a delegate's own entries; filter the Sector Lead separately). */
  enteredById: z.uuid().optional(),
  /** Part of a recipient's name or an award ID ("SDB-2026-01"). */
  q: z.string().trim().min(1).max(100).optional(),
  /** Awards with anyone charged as overhead or G&A. */
  costType: z.enum(['OH', 'GA']).optional(),
  /**
   * Awards with anyone at this point, person by person, as Spend counts them: `paid` (Payroll paid
   * them or the grant is entered), `in_progress` (sent, not yet paid) or `sent` (either).
   */
  lineStatus: z.enum(['sent', 'in_progress', 'paid']).optional(),
};

/** `GET /api/v1/insights/awards`. */
export const RegisterQuery = z
  .object({
    ...registerFilterFields,
    sort: RegisterSort.default('newest'),
    groupBy: RegisterGroupBy.default('none'),
    ...pageFields,
  })
  .strict()
  .superRefine(checkPeriod);
export type RegisterQuery = z.infer<typeof RegisterQuery>;

/** `GET /api/v1/insights/awards/export`: the same view as CSV, every page. */
export const RegisterExportQuery = z
  .object({
    ...registerFilterFields,
    sort: RegisterSort.default('newest'),
    groupBy: RegisterGroupBy.default('none'),
    /** One row per award (the register), or one per person with charge codes (for the GL). */
    grain: z.enum(['award', 'line']).default('award'),
  })
  .strict()
  .superRefine(checkPeriod);
export type RegisterExportQuery = z.infer<typeof RegisterExportQuery>;

/** The detail after the plain stage on X6, in plain words ("With the Stock Admin team · letters"). */
export const RegisterStep = z.enum([
  'waiting_for_stock_admin',
  'checking',
  'letters',
  'released',
  'past_deadline',
  'with_payroll',
  'statement_check',
  'shareworks',
]);
export type RegisterStep = z.infer<typeof RegisterStep>;

/** Who approved it, or who it waits for. Never an internal state. */
export const RegisterApproval = z.discriminatedUnion('status', [
  z.object({ status: z.literal('not_needed') }),
  z.object({
    status: z.literal('waiting'),
    /** The Sector Lead (a delegate's entry), any one Executive, or the Executives outside the app. */
    on: z.enum(['sector_lead', 'named_executive', 'executive', 'executives_outside_app']),
    person: Ref.nullable(),
    /** Waiting because it goes over its pool's budget. */
    overBudget: z.boolean(),
  }),
  z.object({
    status: z.literal('approved'),
    /**
     * Who approved it in the app; for a decision made outside it, the Executive named when it was
     * recorded (else who recorded it). Null for imports from the old tracker.
     */
    by: Ref.nullable(),
    /**
     * APP-148 R140: the Executive or Sector Lead a Stock Admin approved it for; null when it was
     * approved in their own right. Optional: older servers omit it.
     */
    for: Ref.nullable().optional(),
    /** Decided outside the app (Executive Discretionary, or the Board for imported PAAs and TAAs). */
    outsideApp: z.boolean(),
    /** An Executive approved a top-up for it. */
    overBudget: z.boolean(),
  }),
]);
export type RegisterApproval = z.infer<typeof RegisterApproval>;

/** What one pool paid toward an award ("Paid from"). Amounts, never a split fraction. */
export const RegisterPaidFrom = PaidFrom.extend({ valueCents: Cents, people: Count });
export type RegisterPaidFrom = z.infer<typeof RegisterPaidFrom>;

/** One team on an award: the recipients who share a manager, as Team recognition groups them. */
export const RegisterTeam = z.object({
  /** The manager's person ID; null for recipients with no manager listed. */
  managerId: z.uuid().nullable(),
  label: z.string(),
  people: Count,
});
export type RegisterTeam = z.infer<typeof RegisterTeam>;

export const RegisterRow = z.object({
  award: z.object({ id: z.uuid(), displayId: z.string() }),
  /** Loaded from the old tracker ("Imported · old tracker"). */
  imported: z.boolean(),
  /**
   * The award date, or the date it was sent when it has none. Null for a draft sent back with no
   * award date: a draft isn't sent, so it has no date until it's sent again (APP-186). An award
   * cancelled or rejected in that state keeps none.
   */
  awardDate: PlainDate.nullable(),
  /**
   * The organization's day it was sent; null for a draft, a draft sent back included (APP-186).
   * X1's Recent awards lists by when awards were sent (`sent_newest`) and shows this date
   * (APP-054). Optional: older servers.
   */
  sentOn: PlainDate.nullable().optional(),
  /** The budget year it charges. */
  fiscalYear: Year,
  type: AwardTypeCode,
  sector: Ref.nullable(),
  program: Ref.nullable(),
  recipients: z.object({ count: Count, people: z.array(Ref).max(12) }),
  cashCents: Cents,
  options: Count,
  /** Cash plus options at the FMV the award was calculated with (a PAA's total value). */
  valueCents: Cents,
  /** Empty while a returned draft has no one on it. */
  paidFrom: z.array(RegisterPaidFrom),
  enteredBy: Ref,
  /** A delegate's entry: the Sector Lead it was entered for. */
  forPerson: Ref.nullable(),
  approval: RegisterApproval,
  stage: StageId.nullable(),
  ended: z.enum(['rejected', 'cancelled']).nullable(),
  step: RegisterStep.nullable(),
  /** The reader may open the award record (staff only); otherwise the row isn't a link. */
  canOpenRecord: z.boolean(),
  /** The organization's day the award reached Closed; null unless its stage is closed (APP-072). */
  closedOn: PlainDate.nullable(),
  /**
   * The recipients' teams (people who share a manager, by today's directory), most people first,
   * then by name. Empty while a returned draft has no one on it (APP-045 A46).
   */
  teams: z.array(RegisterTeam).optional(),
});
export type RegisterRow = z.infer<typeof RegisterRow>;

const RegisterAmounts = z.object({
  awards: Count,
  /** People on the awards, counted once per award (a person on two awards counts twice). */
  recipients: Count,
  cashCents: Cents,
  options: Count,
  valueCents: Cents,
});

export const RegisterGroup = RegisterAmounts.extend({
  /**
   * Sector ID, award type, or `YYYY-MM`; `none` for awards without a sector. By month, an award
   * with no date is `none` ("No date yet": a draft sent back with no award date) or, once it has
   * ended, `no_award_date` ("No award date"), after the dated months (APP-186).
   */
  key: z.string(),
  label: z.string(),
});
export type RegisterGroup = z.infer<typeof RegisterGroup>;

export const AwardRegister = z.object({
  asOf: PlainDate,
  scope: ReportScope,
  period: ResolvedPeriod,
  /** Any filter beyond the time frame is on. */
  filtersOn: z.boolean(),
  /** Everything in the view, every page. A split TAA counts once, in full. */
  totals: RegisterAmounts.extend({
    /** Different people across the view. */
    people: Count,
  }),
  /**
   * With a pool, person or cost-type filter: what the matching people's lines come to (the part of
   * a split TAA that pool paid). Null without one.
   */
  matched: z
    .object({
      cashCents: Cents,
      options: Count,
      valueCents: Cents,
      /** The matching people (one row each in the by-person export). */
      people: Count,
    })
    .nullable(),
  /** Group totals over every page, in display order. Empty without grouping. */
  groups: z.array(RegisterGroup),
  /** Every month in the time frame, by award date. An award with no date is in none (APP-186). */
  byMonth: z.array(z.object({ month: Month, awards: Count, cashCents: Cents, valueCents: Cents })),
  rows: z.array(RegisterRow),
  page: PageInfo,
  /**
   * "Ties to Budget pools", for a fiscal year with no filters: cash charged to the discretionary
   * pools (the sector pools and the Executive pool) and to the PAA/TAA pool, counted from
   * submission like Budget pools. Null otherwise.
   */
  reconciliation: z
    .object({
      fiscalYear: Year,
      discretionary: Units,
      paaTaa: Units,
      /**
       * Spot awards' part of `discretionary`: they're charged to the sector pools but listed in
       * their own report, not here (A48). Optional: older servers leave it out.
       */
      spot: Units.optional(),
    })
    .nullable(),
  /** What the filters can choose from, within the reader's scope. */
  choices: z.object({
    sectors: z.array(Ref),
    pools: z.array(Ref),
    programs: z.array(Ref.extend({ sectorId: z.string() })),
    enteredBy: z.array(Ref.extend({ forPerson: Ref.nullable() })),
  }),
  /**
   * The person filter (`personId`), named so the hero can say whose awards these are ("For Jordan
   * Ellis"). Only when they received an award the reader may see; null otherwise, so an ID never
   * becomes a directory name. Optional: older servers leave it out (QA-035).
   */
  person: Ref.nullable().optional(),
});
export type AwardRegister = z.infer<typeof AwardRegister>;

// ---------------------------------------------------------------------------------------------
// RPT1 · Overview and report library
// ---------------------------------------------------------------------------------------------

export const LibraryReportId = z.enum([
  'budget_pools',
  'all_awards',
  'heat_map',
  'budgets_history',
  'spot_awards',
  'repeat_recipients',
  'givers',
  'spend',
  'ask_data',
  'team_recognition',
]);
export type LibraryReportId = z.infer<typeof LibraryReportId>;

export const LibraryReport = z.object({
  id: LibraryReportId,
  title: z.string(),
  /** The question the report answers. */
  question: z.string(),
  /** False while a switch keeps it off (Ask the data with AI off); the card says so. */
  available: z.boolean(),
  unavailableReason: z.enum(['ai_off']).nullable(),
});
export type LibraryReport = z.infer<typeof LibraryReport>;

/** A sector's recognition coverage. Groups under five people show counts, not a percent. */
export const CoverageRow = z.object({
  sector: Ref,
  people: Count,
  recognized: Count,
  percent: Percent,
  repeat: Count,
  smallGroup: z.boolean(),
});
export type CoverageRow = z.infer<typeof CoverageRow>;

export const RepeatRule = z.object({
  awards: Count,
  valueCents: Cents,
  rollingMonths: Count,
});

/**
 * The overview's time frame for awards by month and Spot: this budget year to date, the last 12
 * months, or a whole budget year. Pools are always the budget year; recognition coverage always
 * looks back over the repeat rule's months. Left out: the budget year's months to date.
 */
export const OverviewPeriod = z.enum(['year_to_date', 'last_12_months', 'fiscal_year']);
export type OverviewPeriod = z.infer<typeof OverviewPeriod>;

/** `GET /api/v1/insights/overview?fiscalYear=&period=`. */
export const OverviewQuery = z
  .object({ fiscalYear: QueryYear.optional(), period: OverviewPeriod.optional() })
  .strict();
export type OverviewQuery = z.infer<typeof OverviewQuery>;

/** `GET /api/v1/insights/overview/export`: every table on the overview, as CSV. */
export const OverviewExportQuery = OverviewQuery;
export type OverviewExportQuery = z.infer<typeof OverviewExportQuery>;

export const InsightsOverview = z.object({
  asOf: PlainDate,
  fiscalYear: Year,
  scope: ReportScope,
  /** The time frame of awards by month: its kind and award-date bounds (inclusive). */
  window: z.object({ period: OverviewPeriod, from: PlainDate, to: PlainDate }),
  /** Each pool the reader may see, for the year: the numbers on Budget pools. */
  pools: z.array(PoolPosition),
  /** Which of `pools` is the PAA/TAA pool; null when the reader doesn't see it. */
  paaTaaPoolId: z.string().nullable(),
  /** The discretionary pools (sector pools and the Executive pool) together. */
  discretionary: z.object({
    /** Pools with a budget set; null when none is set. */
    budget: Units.nullable(),
    /** Paid and in progress (held from submission), on every discretionary pool. */
    awarded: Units,
    pools: Count,
    poolsWithoutBudget: z.array(Ref),
  }),
  /** Null when the reader can't open the heat map. */
  recognition: z
    .object({
      from: PlainDate,
      to: PlainDate,
      people: Count,
      recognized: Count,
      percent: Percent,
      repeat: Count,
      /** People in the directory with no sector yet: counted in `people`, in no sector row. */
      noSector: Count,
      repeatRule: RepeatRule,
      sectors: z.array(CoverageRow),
    })
    .nullable(),
  /** The year's months to date, by award date: cash awarded (paid and in progress). */
  awardsByMonth: z.array(
    z.object({
      month: Month,
      discretionaryCashCents: Cents,
      paaTaaCashCents: Cents,
      /** Awarded, funded Spot awards by award date; absent on older servers and null when unreadable. */
      spotAwards: Count.nullable().optional(),
    }),
  ),
  /** Null when the reader can't open the Spot report. */
  spot: SpotStatus.nullable(),
  /**
   * The Spot awards in `window` (the same awards as `awardsByMonth[].spotAwards`) and their value,
   * for the Spot card; `spot` is always this budget year to date. Null when the reader can't open
   * the Spot report; absent on older servers.
   */
  spotInWindow: z.object({ awards: Count, valueCents: Cents }).nullable().optional(),
  /** Only the reports this reader may open, in library order. */
  library: z.array(LibraryReport),
});
export type InsightsOverview = z.infer<typeof InsightsOverview>;

// ---------------------------------------------------------------------------------------------
// RPT2 · Recognition heat map
// ---------------------------------------------------------------------------------------------

/** What a heat map's cells show; an export writes the same measure. */
export const HeatCells = z.enum(['count', 'value', 'coverage']);
export type HeatCells = z.infer<typeof HeatCells>;

/** Received, person level only: who to list and how to find them. */
const personFilterFields = {
  /**
   * Everyone in the group, only the people recognized in the window, only those not recognized
   * yet, or only repeat recipients. Left out: everyone when a team is in focus, else the
   * recognized.
   */
  recognized: z.enum(['all', 'recognized', 'not_yet', 'repeat']).optional(),
  /** Part of a name, an EE# or a job title. */
  q: z.string().trim().min(1).max(100).optional(),
  /** People recognized on an award under this program in the window. */
  programId: z.uuid().optional(),
  /** Keep each team's people together (teams by name), then the chosen order. */
  groupByTeam: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
};

export const HeatMapQuery = z
  .object({
    /** Who was recognized, or who gave it (entered the awards). */
    direction: z.enum(['received', 'given']).default('received'),
    /** Given has no team level: rows show each giver. */
    level: z.enum(['sector', 'team', 'person']).default('sector'),
    period: z.enum(['last_12_months', 'fiscal_year']).default('last_12_months'),
    fiscalYear: QueryYear.optional(),
    /** Spot Awards aren't counted: recognition here is the four entry types (K37). */
    types: z.enum(['all', 'discretionary', 'paa_taa']).default('all'),
    sectorId: z.string().min(1).max(100).optional(),
    /** A team: the manager's person ID, or `none` for people with no manager listed. */
    team: z.union([z.uuid(), z.literal('none')]).optional(),
    /**
     * Person and giver rows: ranked by count or by value; people also by their most recent award
     * (never recognized last) or by name. Ties go by name, then ID, so pages never overlap.
     */
    sort: z.enum(['count', 'value', 'recent', 'name']).default('count'),
    ...pageFields,
    ...personFilterFields,
  })
  .strict();
export type HeatMapQuery = z.infer<typeof HeatMapQuery>;

/** `GET /api/v1/insights/heat-map/export`: the view as CSV, every row, with what its cells show. */
export const HeatMapExportQuery = HeatMapQuery.omit({ page: true, size: true })
  .extend({ cells: HeatCells.default('count') })
  .strict();
export type HeatMapExportQuery = z.infer<typeof HeatMapExportQuery>;

export const HeatCell = z.object({
  /** Recognitions (people on awards) received, or given, that month. */
  count: Count,
  valueCents: Cents,
  /** Sector and team rows: the share of the group recognized so far in the window. */
  coveragePercent: Percent,
});
export type HeatCell = z.infer<typeof HeatCell>;

export const HeatRow = z.object({
  /**
   * A sector ID, `team:<sectorId>:<managerId|none>` (open it with `sectorId` and `team`), a
   * person ID, or `company`.
   */
  key: z.string(),
  kind: z.enum(['company', 'sector', 'team', 'person', 'giving_sector', 'giver']),
  label: z.string(),
  sector: Ref.nullable(),
  /** Team rows and person rows: "Tessa Lindgren’s team". */
  team: z.string().nullable(),
  /** Giver rows: the Sector Lead a delegate entered for. */
  forPerson: Ref.nullable(),
  /** The group's current headcount; null for a person or giver. */
  people: Count.nullable(),
  /**
   * Received sector rows (and the company's total): how many teams its people make up, a team
   * being a manager's people (A18), for the board's "212 people · 6 teams". Absent on other rows
   * and on older servers.
   */
  teams: Count.optional(),
  months: z.array(HeatCell),
  totals: z.object({
    count: Count,
    valueCents: Cents,
    monthsActive: Count,
    last: Month.nullable(),
    /** Received groups: people recognized in the window. */
    recognized: Count.nullable(),
    /** Received: repeat recipients in the group (or 1/0 for a person). */
    repeat: Count.nullable(),
    /** Given: different people reached. */
    reached: Count.nullable(),
    /** Given: awards entered. */
    awards: Count.nullable(),
    coveragePercent: Percent,
  }),
  /** Under five people: show counts, not a coverage percent. */
  smallGroup: z.boolean(),
  /** There's a level below to open. */
  drill: z.boolean(),
  /** Received person rows only: who they are and where they stand. Null for any other row. */
  detail: z
    .object({
      eeNumber: z.string().nullable(),
      jobTitle: z.string().nullable(),
      /** The team's key, to group rows and open the team (`team:<sectorId>:<managerId|none>`). */
      teamKey: z.string(),
      /** Their most recent recognition in the window. */
      lastDate: PlainDate.nullable(),
      /** Recognized in the 30 days up to the window's end. */
      recent: z.boolean(),
      /** Awards and value in the repeat rule's months, and whether that makes them a repeat. */
      repeatAwards: Count,
      repeat: z.boolean(),
    })
    .nullable(),
});
export type HeatRow = z.infer<typeof HeatRow>;

export const RepeatRecipient = z.object({
  person: Ref,
  sector: Ref.nullable(),
  team: z.string(),
  awards: Count,
  valueCents: Cents,
  lastDate: PlainDate,
  /** Which part of the rule it met: the number of awards, the value, or both. */
  byCount: z.boolean(),
  byValue: z.boolean(),
});
export type RepeatRecipient = z.infer<typeof RepeatRecipient>;

export const TopGiver = z.object({
  person: Ref,
  forPerson: Ref.nullable(),
  sector: Ref.nullable(),
  role: z.enum([
    'sector_lead',
    'delegate',
    'executive',
    'requester',
    'executive_delegate',
    'other',
  ]),
  /** Awards entered in the window. */
  awards: Count,
  recognitions: Count,
  reached: Count,
  valueCents: Cents,
});
export type TopGiver = z.infer<typeof TopGiver>;

/**
 * A recognition report's window and its month columns: twelve, or thirteen when a fiscal year
 * starts mid-month and its first and last months are both partial (APP-018).
 */
const RecognitionWindow = z.object({
  from: PlainDate,
  to: PlainDate,
  months: z.array(Month).min(12).max(13),
});

export const HeatMap = z.object({
  asOf: PlainDate,
  scope: ReportScope,
  window: RecognitionWindow,
  direction: z.enum(['received', 'given']),
  /** The level shown (Given asked for teams shows givers). */
  level: z.enum(['sector', 'team', 'person']),
  types: z.enum(['all', 'discretionary', 'paa_taa']),
  focus: z.object({
    sector: Ref.nullable(),
    team: z.object({ key: z.string(), label: z.string() }).nullable(),
  }),
  repeatRule: RepeatRule,
  kpis: z.object({
    people: Count,
    recognized: Count,
    percent: Percent,
    repeat: Count,
    recognitions: Count,
    valueCents: Cents,
    awardsEntered: Count,
  }),
  /** Whole authorization-scope coverage, independent of the current focus filter. */
  scopeCoverage: z
    .object({
      people: Count,
      recognized: Count,
      percent: Percent,
    })
    .optional(),
  rows: z.array(HeatRow),
  /** Rows before paging (person and giver levels page). */
  rowCount: Count,
  /** Where this page sits in those rows; sector and team levels show every row on one page. */
  page: PageInfo,
  /** The company, the sector or the team in focus. */
  total: HeatRow,
  repeatRecipients: z.array(RepeatRecipient),
  topGivers: z.array(TopGiver),
  coverage: z.array(CoverageRow),
});
export type HeatMap = z.infer<typeof HeatMap>;

// ---------------------------------------------------------------------------------------------
// SL8 · Team recognition
// ---------------------------------------------------------------------------------------------

const teamFields = {
  /** Required for a reader who may open more than one sector; a Sector Lead's own by default. */
  sectorId: z.string().min(1).max(100).optional(),
  period: z.enum(['last_12_months', 'fiscal_year']).default('last_12_months'),
  fiscalYear: QueryYear.optional(),
  /** Spot Awards aren't counted: recognition here is the four entry types (K37). */
  types: z.enum(['all', 'discretionary', 'paa_taa']).default('all'),
};

/** `GET /api/v1/insights/team`: a Sector Lead's control view of their sector. */
export const TeamRecognitionQuery = z.object(teamFields).strict();
export type TeamRecognitionQuery = z.infer<typeof TeamRecognitionQuery>;

/** One of the most recognized people: ranked by value in the window. */
export const TopRecipient = z.object({
  person: Ref,
  team: z.string(),
  teamKey: z.string(),
  /** Awards and value in the window. */
  awards: Count,
  valueCents: Cents,
  lastDate: PlainDate,
  /** A repeat recipient under the rule (its months, ending with the window). */
  repeat: z.boolean(),
  repeatAwards: Count,
});
export type TopRecipient = z.infer<typeof TopRecipient>;

export const TeamRecognition = z.object({
  asOf: PlainDate,
  scope: ReportScope,
  sector: Ref,
  /** The sectors this reader may choose, in order (one for a Sector Lead of one sector). */
  sectors: z.array(Ref),
  period: z.enum(['last_12_months', 'fiscal_year']),
  types: z.enum(['all', 'discretionary', 'paa_taa']),
  window: RecognitionWindow,
  repeatRule: RepeatRule,
  kpis: z.object({
    people: Count,
    recognized: Count,
    percent: Percent,
    repeat: Count,
    notYet: Count,
    /** The team with the most people not recognized yet; null when everyone has been. */
    mostNotYet: z.object({ team: z.string(), notYet: Count }).nullable(),
    /** People on awards in the window (a person on two awards counts twice). */
    personAwards: Count,
    valueCents: Cents,
    /** The middle person-award value in the window; null before the first. */
    medianCents: Cents.nullable(),
    teams: Count,
  }),
  /** One row per team, by name; each team's people and how many were recognized. */
  teams: z.array(HeatRow),
  /** The whole sector. */
  total: HeatRow,
  /** The ten most recognized by value in the window, with repeat flags. */
  mostRecognized: z.array(TopRecipient).max(10),
  notYet: z.object({
    count: Count,
    people: Count,
    byTeam: z.array(z.object({ key: z.string(), label: z.string(), notYet: Count, people: Count })),
    /** The first few, by name. */
    names: z.array(Ref).max(12),
  }),
  /** The sector's pool for the window's budget year; null when it has none or isn't visible. */
  pool: PoolPosition.nullable(),
  /** Programs on the sector's awards, for the people list's filter. */
  programs: z.array(Ref),
});
export type TeamRecognition = z.infer<typeof TeamRecognition>;

/** `GET /api/v1/insights/team/people`: everyone in the sector, filtered, ordered and paged. */
export const TeamPeopleQuery = z
  .object({
    ...teamFields,
    /** A team: the manager's person ID, or `none` for people with no manager listed. */
    team: z.union([z.uuid(), z.literal('none')]).optional(),
    sort: z.enum(['recent', 'value', 'count', 'name']).default('recent'),
    ...pageFields,
    ...personFilterFields,
  })
  .strict();
export type TeamPeopleQuery = z.infer<typeof TeamPeopleQuery>;

export const TeamPeople = z.object({
  asOf: PlainDate,
  sector: Ref,
  window: RecognitionWindow,
  repeatRule: RepeatRule,
  rows: z.array(HeatRow),
  /** Rows before paging. */
  rowCount: Count,
  page: PageInfo,
});
export type TeamPeople = z.infer<typeof TeamPeople>;

/** `GET /api/v1/insights/team/people/export`: the list as CSV, every row. */
export const TeamPeopleExportQuery = TeamPeopleQuery.omit({ page: true, size: true })
  .extend({ cells: HeatCells.default('value') })
  .strict();
export type TeamPeopleExportQuery = z.infer<typeof TeamPeopleExportQuery>;

// ---------------------------------------------------------------------------------------------
// Saved views (X6)
// ---------------------------------------------------------------------------------------------

/** Reports a person can save views of. */
export const SavedViewReport = z.enum(['all_awards']);
export type SavedViewReport = z.infer<typeof SavedViewReport>;

/**
 * A view as its address holds it: the report's own query names and values (no page cursor). The
 * server checks it against the report's query before saving.
 */
export const SavedViewQuery = z
  .record(z.string().min(1).max(40), z.string().max(200))
  .refine((v) => Object.keys(v).length <= 20, 'Too many filters');

export const SavedView = z.object({
  id: z.uuid(),
  report: SavedViewReport,
  name: z.string(),
  query: SavedViewQuery,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SavedView = z.infer<typeof SavedView>;

export const SavedViewList = z.object({
  views: z.array(SavedView),
  /** How many views a person may keep for each report. */
  limit: Count,
});
export type SavedViewList = z.infer<typeof SavedViewList>;

export const SavedViewListQuery = z.object({ report: SavedViewReport }).strict();
export type SavedViewListQuery = z.infer<typeof SavedViewListQuery>;

const ViewName = z.string().trim().min(1, 'Give the view a name').max(80);

export const SavedViewCreate = z
  .object({ report: SavedViewReport, name: ViewName, query: SavedViewQuery })
  .strict();
export type SavedViewCreate = z.infer<typeof SavedViewCreate>;

/** Rename a view, or save the current filters over it. */
export const SavedViewUpdate = z
  .object({ name: ViewName.optional(), query: SavedViewQuery.optional() })
  .strict()
  .refine((v) => v.name !== undefined || v.query !== undefined, 'Nothing to change');
export type SavedViewUpdate = z.infer<typeof SavedViewUpdate>;

// ---------------------------------------------------------------------------------------------
// RPT3 · Budgets and history
// ---------------------------------------------------------------------------------------------

export const BudgetsHistoryQuery = z
  .object({
    /** Defaults to this year and the two before it. */
    years: many(QueryYear),
    sectorId: z.string().min(1).max(100).optional(),
  })
  .strict();
export type BudgetsHistoryQuery = z.infer<typeof BudgetsHistoryQuery>;

/** `GET /api/v1/insights/budgets/export`: every table on Budgets & history, as CSV. */
export const BudgetsExportQuery = BudgetsHistoryQuery;
export type BudgetsExportQuery = z.infer<typeof BudgetsExportQuery>;

const PoolYear = z.object({
  fiscalYear: Year,
  /** Null when no budget was set that year. */
  budget: Units.nullable(),
  /** Paid and in progress (held from submission), as on Budget pools. */
  awarded: Units,
  inProgress: Units,
  paid: Units,
});

export const PaceYear = z.object({
  fiscalYear: Year,
  /** The cash budget of the discretionary pools that have one; null when none does. */
  budgetCents: Cents.nullable(),
  /** The options budget of the same pools; null when none has one. */
  budgetOptions: Count.nullable(),
  /** Cash and options awarded by each month's end, cumulative. The current year stops this month. */
  months: z.array(
    z.object({
      month: Month,
      cumulativeCents: Cents,
      percent: Percent,
      cumulativeOptions: Count,
      optionsPercent: Percent,
    }),
  ),
});
export type PaceYear = z.infer<typeof PaceYear>;

export const LedgerSummary = z.object({
  pool: Ref,
  initial: Units.nullable(),
  additions: Units,
  reductions: Units,
  movesIn: Units,
  movesOut: Units,
  topUps: Units,
  /** The budget now: every entry that isn't voided. Null when none was ever set. */
  now: Units.nullable(),
  /** Voided entries stay in the ledger and stop counting. */
  voided: Count,
  latest: z
    .object({
      kind: z.enum(['initial', 'addition', 'reduction', 'move_in', 'move_out', 'top_up']),
      at: z.string(),
      /** Null for the deployment's system actor, "MyRewards setup" (APP-118). */
      by: Ref.nullable(),
      reason: z.string().nullable(),
      voided: z.boolean(),
    })
    .nullable(),
  /**
   * Over-budget awards waiting on an Executive this year, and the top-up each needs from this pool
   * (what brings it back to zero, as the approval offers it). Not in the ledger until approved.
   */
  waiting: z
    .array(
      z.object({
        /** The award; null for a Spot award, which has no award ID until it's funded. */
        award: z.object({ id: z.uuid(), displayId: z.string() }).nullable(),
        /** A Spot award's funding sent to an Executive. It never names the nominee (A48). */
        spot: z.boolean(),
        topUp: Units,
      }),
    )
    .optional(),
});
export type LedgerSummary = z.infer<typeof LedgerSummary>;

export const BudgetsHistory = z.object({
  asOf: PlainDate,
  fiscalYear: Year,
  scope: ReportScope,
  /** Oldest first. The current year is to date. */
  years: z.array(
    z.object({
      fiscalYear: Year,
      toDate: z.boolean(),
      /** The sector pools and the Executive pool. */
      discretionary: PoolYear.omit({ fiscalYear: true }),
      /** Null when the reader doesn't see the PAA/TAA pool. */
      paaTaa: PoolYear.omit({ fiscalYear: true }).nullable(),
    }),
  ),
  pools: z.array(
    z.object({
      pool: Ref,
      kind: z.enum(['sector', 'executive', 'paa_taa', 'spot']),
      sector: Ref.nullable(),
      years: z.array(PoolYear),
    }),
  ),
  /** This year against last year to the same date, discretionary pools, cash. */
  pace: z.object({
    current: PaceYear,
    prior: PaceYear,
    sameDay: z.object({
      date: PlainDate,
      priorDate: PlainDate,
      currentPercent: Percent,
      priorPercent: Percent,
      currentOptionsPercent: Percent,
      priorOptionsPercent: Percent,
    }),
  }),
  /**
   * Cash awarded this year from each sector's own pool, per person in the sector now. The
   * Executive pool counts under Corporate. PAA/TAA awards aren't split by sector here.
   */
  perEmployee: z.object({
    /** Null for a reader who sees only their own sector. */
    company: z
      .object({
        people: Count,
        awardedCents: Cents,
        perPersonCents: Cents.nullable(),
        awardedOptions: Count,
        /** Options per person, in tenths (37.5 → 375), rounded half up. */
        perPersonOptionsTenths: Count.nullable(),
      })
      .nullable(),
    sectors: z.array(
      z.object({
        sector: Ref,
        pool: Ref.nullable(),
        people: Count,
        awardedCents: Cents,
        perPersonCents: Cents.nullable(),
        awardedOptions: Count,
        perPersonOptionsTenths: Count.nullable(),
      }),
    ),
    /**
     * Spot awards per 100 people this year (Award intensity's second panel), counted as RPT4
     * counts them (A41): by the sector that funded each award, over the sector's people now, so
     * each figure equals the Spot awards report's. Only for a reader who can open that report
     * (`spot` isn't null); absent otherwise and on older servers.
     */
    spot: z
      .object({
        /**
         * Every Spot award in scope over everyone in the directory, as RPT4's hero counts the
         * company; null for a reader narrowed to one sector.
         */
        // APP-197: null for fewer than SMALL_GROUP people (the counts are beside it).
        company: z
          .object({ people: Count, awards: Count, per100Tenths: Count.nullable() })
          .nullable(),
        sectors: z.array(
          z.object({ sector: Ref, people: Count, awards: Count, per100Tenths: Count.nullable() }),
        ),
      })
      .optional(),
  }),
  ledger: z.object({ fiscalYear: Year, pools: z.array(LedgerSummary) }),
  spot: SpotStatus.nullable(),
});
export type BudgetsHistory = z.infer<typeof BudgetsHistory>;

// ---------------------------------------------------------------------------------------------
// F1 · Spend
// ---------------------------------------------------------------------------------------------

export const SpendQuery = z
  .object({
    fiscalYear: QueryYear.optional(),
    poolId: z.string().min(1).max(100).optional(),
    type: many(AwardTypeCode),
    /** Paid (gross cash on paid lines), in progress (sent, not yet paid), or both. */
    status: z.enum(['all', 'paid', 'in_progress']).default('all'),
    costType: z.enum(['OH', 'GA']).optional(),
  })
  .strict();
export type SpendQuery = z.infer<typeof SpendQuery>;

export const ChargeCodeSpend = z.object({
  costType: z.enum(['OH', 'GA']),
  account: z.string(),
  /** Text: 15125.110 keeps its zero. Null for PAA/TAA lines, which carry no project. */
  project: z.string().nullable(),
  /** Null when each person's home org is used (PAA/TAA). */
  org: z.string().nullable(),
  homeOrg: z.boolean(),
  /** The pool the project belongs to, when there's one. */
  pool: Ref.nullable(),
  paidCashCents: Cents,
  inProgressCashCents: Cents,
});
export type ChargeCodeSpend = z.infer<typeof ChargeCodeSpend>;

export const Spend = z.object({
  asOf: PlainDate,
  fiscalYear: Year,
  scope: ReportScope,
  status: z.enum(['all', 'paid', 'in_progress']),
  filtersOn: z.boolean(),
  kpis: z.object({
    /** Gross cash on lines Payroll has paid, this year. */
    paidCashCents: Cents,
    /** Sent and not yet paid: what Budget pools shows in progress. */
    inProgressCashCents: Cents,
    /** Pools with a budget set this year: what's left. Null when none is set. */
    budgetLeft: z.object({ cashCents: Cents, pools: Count }).nullable(),
    options: z.object({ granted: Count, valueCents: Cents }),
    average: z.object({ cashCents: Cents.nullable(), awards: Count, people: Count }),
  }),
  /** Budget against actual for each pool the reader sees (filters don't apply). */
  budgetVsActual: z.array(PoolPosition),
  byType: z.array(
    z.object({ group: z.enum(['discretionary', 'paa', 'taa']), cashCents: Cents, awards: Count }),
  ),
  /** The Sector Lead (with their delegates' entries) or whoever entered it. */
  byGranter: z.array(
    z.object({
      person: Ref,
      sector: Ref.nullable(),
      delegates: z.array(Ref),
      cashCents: Cents,
      awards: Count,
    }),
  ),
  /** Gross cash paid by the month Payroll paid it. Every month of the year to date. */
  byMonth: z.array(z.object({ month: Month, paidCashCents: Cents })),
  /** By project number (digits by their value), then account, org, OH before G&A; no project last. */
  byChargeCode: z.array(ChargeCodeSpend),
  topRecipients: z.array(
    z.object({ person: Ref, sector: Ref.nullable(), awards: Count, cashCents: Cents }),
  ),
});
export type Spend = z.infer<typeof Spend>;

// ---------------------------------------------------------------------------------------------
// RPT4 · Spot awards
// ---------------------------------------------------------------------------------------------

/**
 * A Spot award's Areté value in RPT4, or `none` (APP-240, Q9): the value became optional, so an
 * award sent without one is counted, filtered and listed as "No value chosen", never dropped.
 */
export const SpotReportValue = z.enum([...AreteValue.options, 'none']);
export type SpotReportValue = z.infer<typeof SpotReportValue>;

/** "Every Spot award" filters (RPT4). They narrow the list only; the charts above keep the year. */
const spotListFields = {
  /** The sector that funded the award. */
  sectorId: z.string().min(1).max(100).optional(),
  value: SpotReportValue.optional(),
  /** Whole dollars, as the screen offers them; inclusive. */
  amountFrom: z.coerce.number().int().min(0).max(1_000_000).optional(),
  amountTo: z.coerce.number().int().min(0).max(1_000_000).optional(),
  /** Part of the nominator's name. */
  nominatedBy: z.string().trim().min(1).max(100).optional(),
  /** Only people with two or more Spot awards this year ("Show only them below"). */
  onlyRepeat: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
};

export const SpotReportQuery = z
  .object({ fiscalYear: QueryYear.optional(), ...spotListFields })
  .strict();
export type SpotReportQuery = z.infer<typeof SpotReportQuery>;

/** `GET /api/v1/insights/spot/export`: the year's Spot awards in the view, as CSV. */
export const SpotExportQuery = SpotReportQuery;
export type SpotExportQuery = z.infer<typeof SpotExportQuery>;

/**
 * Whether a Spot award's citation can be shown (APP-049): only the committee's recipient summary,
 * only when it chose to publish it, and only once the award is released. Never the nomination.
 */
export const SpotCitationState = z.enum(['published', 'private', 'after_release']);
export type SpotCitationState = z.infer<typeof SpotCitationState>;

/** One Spot Award: a nominee the committee and supervisor awarded, with its funded cash line. */
export const SpotAwardRow = z.object({
  id: z.string(),
  /** The award ID ("SPOT-2026-0003"). Null for an imported row. */
  displayId: z.string().nullable(),
  date: PlainDate,
  recipient: Ref,
  /** The recipient's department in the directory. */
  department: z.string().nullable(),
  sector: Ref.nullable(),
  nominatedBy: Ref,
  value: SpotReportValue,
  amountCents: Cents,
  /** The published summary; null unless `citationState` is `published`. */
  citation: z.string().nullable(),
  citationState: SpotCitationState,
  /** Where it is: the plain stage and step of its cash line (null for an imported row). */
  stage: RegisterStage.nullable(),
  step: RegisterStep.nullable(),
  imported: z.boolean(),
});
export type SpotAwardRow = z.infer<typeof SpotAwardRow>;

export const SpotReport = z.object({
  asOf: PlainDate,
  fiscalYear: Year,
  scope: ReportScope,
  status: SpotStatus,
  kpis: z.object({
    awards: Count,
    valueCents: Cents,
    averageCents: Cents.nullable(),
    recognized: Count,
    /** The directory headcount the per-100 figures divide by. */
    people: Count,
  }),
  /** This year's Spot awards by the sector that funded them, over each sector's people now. */
  sectors: z.array(
    z.object({
      sector: Ref,
      people: Count,
      awards: Count,
      valueCents: Cents,
      /** Spot Awards per 100 people, to one decimal place, as tenths (103 = 10.3). */
      /** Null for fewer than SMALL_GROUP people (APP-197). */
      per100Tenths: Count.nullable(),
    }),
  ),
  byMonth: z.array(z.object({ month: Month, awards: Count, valueCents: Cents })),
  /** This year's awards by value; `none` only when an award has no value. */
  values: z.array(z.object({ value: SpotReportValue, awards: Count })),
  /** The Spot cash range, when it's set up. */
  range: z.object({ minCents: Cents, maxCents: Cents }).nullable(),
  /**
   * People with two or more Spot awards this year. A Spot-only list: Spot stays out of the
   * repeat-recipient rule everywhere else (APP-049, K37).
   */
  repeatRecipients: z.array(
    z.object({
      person: Ref,
      awards: Count,
      valueCents: Cents,
      department: z.string().nullable().optional(),
      sector: Ref.nullable().optional(),
      /** Each of their awards this year, oldest first. */
      detail: z
        .array(z.object({ date: PlainDate, value: SpotReportValue, amountCents: Cents }))
        .optional(),
    }),
  ),
  /** The year's awards in the view (the list's filters applied), newest first; at most 500. */
  awards: z.array(SpotAwardRow),
  /** How many awards match the list's filters (the list stops at 500). */
  awardsMatching: Count.optional(),
  /** This year's awards by amount, in the range's $500 steps. */
  amounts: z.array(z.object({ amountCents: Cents, awards: Count })).optional(),
  /** Every recorded year's awards by Areté value ("Since …"); `values` is this year's. */
  valuesAllYears: z.array(z.object({ value: SpotReportValue, awards: Count })).optional(),
  /** Of every recorded year's awards (`valuesAllYears`), how many came from the old Spot tracker. */
  importedAwards: Count.optional(),
  /**
   * Up to two budget years before this one, oldest first and never before the first recorded one,
   * month by month for the By month chart's earlier lines. Each year's months run from its own
   * fiscal start (APP-018), plus any month its awards are dated in. `importedAwards` says how many
   * of the year's awards came from the old Spot tracker. Absent on older servers.
   */
  earlierYears: z
    .array(
      z.object({
        fiscalYear: Year,
        awards: Count,
        valueCents: Cents,
        importedAwards: Count,
        months: z.array(z.object({ month: Month, awards: Count, valueCents: Cents })),
      }),
    )
    .optional(),
  /** The first year with a recorded Spot award; null before any. */
  firstYear: Year.nullable().optional(),
  /** The amount given most often this year; null before any. */
  mostOftenCents: Cents.nullable().optional(),
  /**
   * Last year's Spot awards to the same date (the board's "vs 2025 pace"), for this year's count
   * and value to compare with. Null when last year has no recorded Spot award (A49).
   */
  pace: z
    .object({ fiscalYear: Year, through: PlainDate, awards: Count, valueCents: Cents })
    .nullable()
    .optional(),
});
export type SpotReport = z.infer<typeof SpotReport>;

// --- Final summaries (PRD §10.2(8), N2 f, X1; APP-072) ------------------------------------------

/**
 * What an award did to one pool that paid it, as of the close: a snapshot taken when it closed and
 * never recomputed. `budget` and `left` are null when the pool had no budget that year. Options are
 * whole options.
 */
export const PoolImpact = z.object({
  pool: Ref,
  fiscalYear: Year,
  /** What this award took from the pool. */
  paid: Units,
  budget: Units.nullable(),
  /** The year's awarded so far at close (paid and in progress), this award included. */
  awarded: Units,
  /** What was left in the pool before this award and after it. */
  left: z.object({ before: Units, after: Units }).nullable(),
});
export type PoolImpact = z.infer<typeof PoolImpact>;

export const FinalSummaryPerson = z.object({
  person: Ref,
  /** The directory's department. */
  department: z.string().nullable(),
  /** Charged to G&A rather than overhead. */
  ga: z.boolean(),
  cashCents: Cents,
  options: Count,
});
export type FinalSummaryPerson = z.infer<typeof FinalSummaryPerson>;

/**
 * The final summary the Executives get when an Executive Discretionary Bonus closes: people,
 * amounts, each pool before and after, and how it got there. From the latest closing of the award.
 */
export const FinalSummary = z.object({
  award: z.object({ id: z.uuid(), displayId: z.string() }),
  title: z.string().nullable(),
  closedOn: PlainDate,
  fiscalYear: Year,
  /** Who requested it (entered it). */
  requestedBy: Ref,
  /** Entered for someone else: who it was for. */
  forPerson: Ref.nullable(),
  people: z.array(FinalSummaryPerson),
  totals: z.object({ people: Count, cashCents: Cents, options: Count }),
  /**
   * False for an award closed before final summaries were kept (seeded or imported history):
   * `poolImpact` is empty, and screens say the impact wasn't recorded, never a blank or a dash.
   */
  impactRecorded: z.boolean(),
  poolImpact: z.array(PoolImpact),
  /**
   * The Executives decided outside the app; this is what was recorded in it: the Executive named,
   * who recorded the decision and for whom, the day, and whether the proof is attached.
   */
  decision: z.object({
    /**
     * True when an Executive who isn't on it approved it in the app (the requester is on it,
     * APP-055): `executive` and `recordedBy` are that Executive, and `recordedFor` is null.
     */
    inApp: z.boolean(),
    executive: Ref.nullable(),
    recordedBy: Ref.nullable(),
    recordedFor: Ref.nullable(),
    decidedOn: PlainDate.nullable(),
    proofAttached: z.boolean(),
  }),
  /** When the letters were released. Who signs them is the letter library's (APP-116). */
  letters: z.object({ releasedOn: PlainDate.nullable() }),
  paid: z.object({
    paidOn: PlainDate.nullable(),
    by: Ref.nullable(),
    statementsAttached: z.boolean(),
  }),
  canOpenRecord: z.boolean(),
});
export type FinalSummary = z.infer<typeof FinalSummary>;

/** `GET /insights/final-summaries`: newest close first, a page at a time. */
export const FinalSummariesQuery = z
  .object({
    limit: z.coerce.number().int().min(1).max(50).default(10),
    /** From `next`: where the previous page ended. */
    cursor: z.string().max(200).optional(),
  })
  .strict();
export type FinalSummariesQuery = z.infer<typeof FinalSummariesQuery>;

export const FinalSummaries = z.object({
  asOf: PlainDate,
  scope: ReportScope,
  summaries: z.array(FinalSummary),
  next: z.string().nullable(),
});
export type FinalSummaries = z.infer<typeof FinalSummaries>;
